import React, {useCallback, useContext, useMemo, useReducer, useState} from 'react'
import {
    Alert,
    Button, Checkbox,
    CircularProgress, FormControl, FormControlLabel, FormGroup,
    Grid,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Typography,
} from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import withStyles from '@mui/styles/withStyles'
import axios, {AxiosResponse, CancelTokenSource} from "axios";
import {debounce} from "lodash";
import {Product} from "../../contexts/types";
import {AppContext} from "../../contexts/AppContextProvider";
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import http from "../../http";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            width: '100%',
        },
        pagination: {
            marginTop: '20px',
        },
        buttons: {
            '& > *': {
                margin: theme.spacing(1),
            },
        },
    })
)

const StyledTableCell = withStyles((theme: Theme) =>
    createStyles({
        head: {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.common.black,
        },
        body: {
            fontSize: 12,
            fontWeight: 'bold',
        },
    })
)(TableCell)

const StyledTableRow = withStyles((theme: Theme) =>
    createStyles({
        root: {
            '&:nth-of-type(odd)': {
                backgroundColor: theme.palette.action.hover,
            },
        },
    })
)(TableRow)

type Paging = {
    pageSize: number
    page: number
    size: number
}

type ProductSearchResponse = {
    paging: Paging
    data: Product[]
}

type ProductSearchInputProps = {
    value?: Product
    onChange: (product: Product) => void
    searchOldProducts?: boolean
    searchCurrentProducts?: boolean
    isProductUnknown?: boolean
    setIsProductUnknown?: React.Dispatch<boolean>
}

type HttpState<TRequest, TResponse> = {
    request: TRequest
    response: TResponse
    loading: boolean
    error: boolean
    errorMessage?: string
}

type HttpAction<TResponse> =
    { type: 'search', payload: { page: number, value: string, cancelToken: CancelTokenSource } } |
    { type: 'error', payload: { message: string } } |
    { type: 'complete', payload: TResponse }

type ProductSearchRequest = {
    searchTerm: string
    page: number
    pageSize: number,
    cancelToken?: CancelTokenSource
}

const initialProductSearchState: HttpState<ProductSearchRequest, ProductSearchResponse> = {
    request: {
        searchTerm: '',
        pageSize: 10,
        page: 1,
        cancelToken: undefined
    },
    response: {
        data: [],
        paging: {page: 1, pageSize: 10, size: 0}
    },
    loading: false,
    error: false,
    errorMessage: undefined
}

const ProductSearchReducer: React.Reducer<HttpState<ProductSearchRequest, ProductSearchResponse>, HttpAction<ProductSearchResponse>> = (state, action) => {
    switch (action.type) {
        case 'search':
            return {
                ...state,
                loading: true,
                error: false,
                errorMessage: undefined,
                request: {
                    ...state.request,
                    page: action.payload.page,
                    searchTerm: action.payload.value,
                    cancelToken: action.payload.cancelToken
                }
            }
        case 'complete':
            return {...state, loading: false, error: false, errorMessage: undefined, response: action.payload}
        case 'error':
            return {
                ...state,
                loading: false,
                data: {data: [], paging: {page: 1, pageSize: 10, size: 0}},
                error: true,
                errorMessage: action.payload.message
            }
        default:
            return state
    }
}

const ProductSearchInput: React.FC<ProductSearchInputProps> = ({
                                                                   value,
                                                                   onChange,
                                                                   searchOldProducts,
                                                                   searchCurrentProducts,
                                                                   isProductUnknown,
                                                                   setIsProductUnknown
                                                               }) => {
    const {countryCode, customerNumber} = useContext(AppContext)
    const {t, itemSearchConfiguration} = useContext(OrgCustomizationContext)
    const classes = useStyles()
    const [search, dispatch] = useReducer(ProductSearchReducer, {
        ...initialProductSearchState,
        response: {
            ...initialProductSearchState.response,
            data: value ? [value] : initialProductSearchState.response.data
        }
    })
    const [showUnknownOption, setShowUnknownOption] = useState<boolean>(false)
    const unknownProductEnabled = useMemo(() => !!setIsProductUnknown, [setIsProductUnknown])

    const handleSearch = useCallback(debounce(async (value: string, page: number) => {
        if (value.toLowerCase() === search.request.searchTerm.toLowerCase() && page === search.request.page) return

        search.request.cancelToken?.cancel()

        const cancelToken = axios.CancelToken.source()

        dispatch({type: 'search', payload: {page, value, cancelToken}})

        try {
            let cpr: AxiosResponse<ProductSearchResponse> | null = null
            let opr: AxiosResponse<ProductSearchResponse> | null = null

            if (searchCurrentProducts && itemSearchConfiguration.useActiveProductSearch(value)) {
                cpr = await http.get<ProductSearchResponse>(`/api/Product/Search?pageSize=${search.request.pageSize}&page=${page}&search=${value}&countryCode=${countryCode}`, {cancelToken: cancelToken.token})
                if (cpr?.data.data) cpr.data.data = cpr.data.data.map(p => ({...p, isDiscontinuedOrUnknown: false}))
            }

            if (searchOldProducts && itemSearchConfiguration.useItemIdSearch(value)) {
                opr = await http.get<ProductSearchResponse>(`/api/Product/SearchDiscontinued?material=${value}&customerNumber=${customerNumber}&countryCode=${countryCode}`, {cancelToken: cancelToken.token})
                if (opr?.data.data) opr.data.data = opr.data.data.map(p => ({...p, isDiscontinuedOrUnknown: true}))
            }

            let res = (cpr?.data.data.length ? cpr?.data : opr?.data) || {
                data: [],
                paging: {page: 0, pageSize: 0, size: 0}
            }

            dispatch({type: 'complete', payload: res})
            setShowUnknownOption(!res.data.length)
        } catch (e) {
            dispatch({type: 'error', payload: {message: 'Unable to load products'}})
        }
    }, 2000), [search, dispatch, setShowUnknownOption, countryCode, customerNumber])

    return (
        <>
            <Grid item xs={12}>
                <Typography variant='h6' sx={{mb: 3}}>{t('ps_enter_product_number')}</Typography>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    label={t('aps_search_product_characters')}
                    id='search'
                    variant='outlined'
                    fullWidth
                    defaultValue={value?.productId}
                    onChange={e => handleSearch(e.target.value, 1)}
                    InputProps={{endAdornment: <>{search.loading && <CircularProgress size={20}/>}</>}}
                />
            </Grid>

            {unknownProductEnabled && showUnknownOption && (
                <Grid item xs={12} sx={{pt: 2}}>
                    <FormControl component='fieldset'>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color='primary'
                                        checked={isProductUnknown}
                                        onChange={(e) => setIsProductUnknown && setIsProductUnknown(!isProductUnknown)}
                                    />
                                }
                                label={t('ps_product_number_unknown') as string}
                            />
                        </FormGroup>
                    </FormControl>
                </Grid>
            )}

            {!search.loading && search.response.data.length === 0 && !!search.request.searchTerm && (
                <Grid item xs={12} sx={{pt: 2}}>
                    <Alert severity='error'>{t('ps_no_product_found')}</Alert>
                </Grid>
            )}

            <Grid item xs={12}>
                {!!search.response.data.length &&
                    <>
                        <Grid item xs={12}>
                            <TableContainer component={Paper} sx={{mt: 3}}>
                                <Table className={classes.table} aria-label='customized table'>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>
                                                {' '}
                                                {t('description')}
                                            </StyledTableCell>
                                            <StyledTableCell align='right'>
                                                {t('material_number')}
                                            </StyledTableCell>
                                            <StyledTableCell align='right'></StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {search.response.data.map((item: Product) => (
                                            <StyledTableRow key={item.productId}>
                                                <StyledTableCell component='th' scope='row'>
                                                    {item.shortDescription}
                                                </StyledTableCell>
                                                <StyledTableCell align='right'>
                                                    {item.productId}
                                                </StyledTableCell>
                                                <StyledTableCell align='right'>
                                                    <Button
                                                        variant='contained'
                                                        size='large'
                                                        color='primary'
                                                        onClick={() => onChange(item)}
                                                    >
                                                        {t('select')}
                                                    </Button>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>

                        {search.response.paging.size > 10 &&
                            <Grid container direction='row' justifyContent='flex-end' item
                                  className={classes.pagination}>
                                <Pagination
                                    color='primary'
                                    shape='rounded'
                                    count={Math.round(search.response.paging.size / search.response.paging.pageSize)}
                                    onChange={(_, newPage) => handleSearch(search.request.searchTerm, newPage)}
                                />
                            </Grid>
                        }
                    </>
                }
            </Grid>
        </>
    )
}

export default ProductSearchInput
